import React, { useEffect, useState, useRef } from 'react'
import { Loader } from 'semantic-ui-react'
// import { Confirm } from 'semantic-ui-react' // Commented out due to delete functionality being disabled
import { DataCategoriesService } from '../../../services/dataCategories'
import {
  DataSourcesService,
  type DataSource as APIDataSource,
} from '../../../services/dataSources'
import { useAuthStore } from '../../../store/authStore'
import type { DataCategory } from '../../../types'
import { extractErrorMessage } from '../../../utils/errorHandler'
import './ConfigureDataSources.css'

// Ingestion method display mapping
const INGESTION_METHOD_DISPLAY_MAP: Record<string, string> = {
  'S3 CSV': 'CSV File Import',
  'File Upload': 'File Upload',
  'SFTP Transfer': 'SFTP Transfer',
  'Real-time API': 'Real-time API',
}

// Reverse mapping for API calls (display name -> API value)
const INGESTION_METHOD_API_MAP: Record<string, string> = {
  'CSV File Import': 'S3 CSV',
  'File Upload': 'File Upload',
  'SFTP Transfer': 'SFTP Transfer',
  'Real-time API': 'Real-time API',
}

// Data category display mapping
const DATA_CATEGORY_DISPLAY_MAP: Record<string, string> = {
  'Accounts & Relationship': 'Customer & Accounts data',
  'Portfolio & Market Data': 'Customer Portfolio data',
}


// Helper functions
const getDisplayIngestionMethod = (apiValue: string): string => {
  return INGESTION_METHOD_DISPLAY_MAP[apiValue] || apiValue
}

export const getApiIngestionMethod = (displayValue: string): string => {
  return INGESTION_METHOD_API_MAP[displayValue] || displayValue
}

const getDisplayDataCategory = (apiValue: string): string => {
  return DATA_CATEGORY_DISPLAY_MAP[apiValue] || apiValue
}



export interface DataSourceCard {
  id: string
  name: string
  shortName: string
  description: string
  category: string
  categoryColor: string
  status: 'connected' | 'available'
  statusColor: string
  endpoint?: string
  ingestionMethod?: string
  lastSync?: string
  totalRecords?: number
  // API fields
  apiId?: number
  sourceId?: number
  // Additional API fields from response
  source_name?: string
  data_category_id?: number
  update_frequency?: string | null
  source_location?: string | null
  context?: string | null
  connection_status?: string | null
  priority?: number | null
  last_sync_utc?: string | null
  created_at?: string
  updated_at?: string
  created_by?: string
  updated_by?: string
  deleted_at?: string | null
  deleted_by?: string | null
  category_name?: string
  category_code?: string
  category_type?: string
  source_type_id?: number
  source_type_code?: string
  source_type_display_name?: string
  source_type_category?: string
  first_upload_id?: number | null
  arrival_schedule?: {
    schedule_type: 'daily' | 'weekly'
    daily_time?: string
    weekly_schedule?: {
      monday: { enabled: boolean; time?: string }
      tuesday: { enabled: boolean; time?: string }
      wednesday: { enabled: boolean; time?: string }
      thursday: { enabled: boolean; time?: string }
      friday: { enabled: boolean; time?: string }
      saturday: { enabled: boolean; time?: string }
      sunday: { enabled: boolean; time?: string }
    }
    timezone: string
  } | null
}

interface PriorityDataSource {
  id: string
  shortName: string
  fullName: string
  description: string
  priority: number | null | undefined
}

export interface ConfigureDataSourcesProps {
  onShowAddCustomDataSource?: () => void
  onShowDataSourceDetails?: (dataSource: DataSourceCard) => void
}

const ConfigureDataSources = ({
  onShowAddCustomDataSource,
  onShowDataSourceDetails,
}: ConfigureDataSourcesProps) => {
  const { isReadOnlyMode } = useAuthStore()
  const [dataSources, setDataSources] = useState<DataSourceCard[]>([])
  const [searchQuery, setSearchQuery] = useState<string>('')

  const [selectedDataSource, setSelectedDataSource] = useState<string>('')
  const [draggedItem, setDraggedItem] = useState<string | null>(null)
  const [dragOverItem, setDragOverItem] = useState<string | null>(null)
  const [isUpdatingPriorities, setIsUpdatingPriorities] =
    useState<boolean>(false)
  const [priorityUpdateError, setPriorityUpdateError] = useState<string>('')
  const [priorityUpdateSuccess, setPriorityUpdateSuccess] = useState<string>('')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)
  const [originalPriorityOrder, setOriginalPriorityOrder] = useState<
    PriorityDataSource[]
  >([])

  const [dataCategories, setDataCategories] = useState<DataCategory[]>([])
  const [isLoadingDataSources, setIsLoadingDataSources] = useState(true)
  const [isHandlingSourceClick, setIsHandlingSourceClick] = useState(false)
  const [hasInitializedData, setHasInitializedData] = useState(false)
  const isInitializingRef = useRef(false)

  // Filtered data sources based on search query
  const filteredDataSources = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return dataSources
    }

    const query = searchQuery.toLowerCase().trim()
    return dataSources.filter(source => {
      const matchesId = source.id.toLowerCase().includes(query)
      const matchesName = source.name.toLowerCase().includes(query)
      const matchesSourceName = source.source_name
        ?.toLowerCase()
        .includes(query)
      const matchesApiId = source.apiId?.toString().includes(query)

      return matchesId || matchesName || matchesSourceName || matchesApiId
    })
  }, [dataSources, searchQuery])

  // Filtered priority data sources based on selected category
  const filteredPriorityDataSources = React.useMemo(() => {
    if (!selectedDataSource) {
      return []
    }

    // Filter data sources that match the selected category - this is the ONLY source of truth
    const matchingDataSources = dataSources.filter(
      source => source.category_name === selectedDataSource
    )

    // Convert to PriorityDataSource format directly from matching data sources
    const result = matchingDataSources.map(source => ({
      id: source.id,
      shortName: source.shortName,
      fullName: source.name,
      description: source.description,
      priority: source.priority,
    }))

    // Sort by priority in ascending order using API values, null values at end
    return result.sort((a, b) => {
      // If both have priority, sort ascending
      if (a.priority && b.priority) return a.priority - b.priority
      // If only a has priority, a comes first
      if (a.priority && !b.priority) return -1
      // If only b has priority, b comes first
      if (!a.priority && b.priority) return 1
      // If neither has priority, maintain order
      return 0
    })
  }, [selectedDataSource, dataSources])
  // TODO: Use these when implementing loading states in UI
  // const [isLoadingCategories, setIsLoadingCategories] = useState(false)

  // Store original order when data changes or category selection changes
  useEffect(() => {
    if (filteredPriorityDataSources.length > 0 && !hasUnsavedChanges) {
      setOriginalPriorityOrder([...filteredPriorityDataSources])
    }
  }, [selectedDataSource, dataSources, isLoadingDataSources, hasUnsavedChanges])

  // Reset unsaved changes when category changes
  useEffect(() => {
    setHasUnsavedChanges(false)
    setPriorityUpdateError('')
    setPriorityUpdateSuccess('')
  }, [selectedDataSource])

  // Fetch data categories and data sources from API
  useEffect(() => {
    // Prevent multiple API calls during initial load
    if (hasInitializedData || isInitializingRef.current) {
      return
    }

    // Mark as initializing to prevent concurrent calls
    isInitializingRef.current = true

    const fetchData = async () => {
      try {
        setIsLoadingDataSources(true)
        // Fetch categories
        const categoriesResponse =
          await DataCategoriesService.getDataCategories()
        if (categoriesResponse.data && Array.isArray(categoriesResponse.data)) {
          setDataCategories(categoriesResponse.data)
          // Set "Accounts & Relationship" as default if available
          if (categoriesResponse.data.length > 0) {
            // Find "Accounts & Relationship" category (displays as "Customer & Accounts data")
            const accountsCategory = categoriesResponse.data.find(cat => cat.name === 'Accounts & Relationship')

            if (accountsCategory) {
              // Use Accounts & Relationship as default
              setSelectedDataSource(accountsCategory.name)
            } else {
              // Find first category that isn't customer identity, transaction, risk, or compliance
              const validCategory = categoriesResponse.data.find(cat => {
                const categoryName = cat.name.toLowerCase()
                return !categoryName.includes('customer') &&
                       !categoryName.includes('identity') &&
                       !categoryName.includes('transaction') &&
                       !categoryName.includes('risk') &&
                       !categoryName.includes('compliance')
              })

              if (validCategory) {
                setSelectedDataSource(validCategory.name)
              }
            }
          }
        }

        // Fetch data sources
        const sourcesResponse = await DataSourcesService.getDataSources()
        if (sourcesResponse.data) {
          // Function to get category color based on category name
          const getCategoryColor = (categoryName: string) => {
            const normalizedCategory = categoryName?.trim() || ''

            // Exact matching for the 5 main categories
            if (normalizedCategory === 'Customer & Identity') {
              return 'customer-identity'
            } else if (normalizedCategory === 'Accounts & Relationship') {
              return 'accounts-relationship'
            } else if (normalizedCategory === 'Transactions') {
              return 'transactional-data'
            } else if (normalizedCategory === 'Risk & Compliance') {
              return 'risk-compliance'
            } else if (normalizedCategory === 'Portfolio & Market Data') {
              return 'portfolio-market'
            }

            // Fallback: partial matching if exact match fails
            const upperCategory = normalizedCategory.toUpperCase()
            if (
              upperCategory.includes('CUSTOMER') ||
              upperCategory.includes('IDENTITY')
            ) {
              return 'customer-identity'
            } else if (
              upperCategory.includes('ACCOUNT') ||
              upperCategory.includes('RELATIONSHIP')
            ) {
              return 'accounts-relationship'
            } else if (upperCategory.includes('TRANSACTION')) {
              return 'transactional-data'
            } else if (
              upperCategory.includes('RISK') ||
              upperCategory.includes('COMPLIANCE')
            ) {
              return 'risk-compliance'
            } else if (
              upperCategory.includes('PORTFOLIO') ||
              upperCategory.includes('MARKET')
            ) {
              return 'portfolio-market'
            }

            return 'teal' // default
          }

          // Transform API data to UI format
          const transformedSources = sourcesResponse.data.map(
            (source: APIDataSource) =>
              ({
                id: source.id.toString(),
                name: source.source_name,
                shortName:
                  source.abbreviation ||
                  source.source_name.substring(0, 3).toUpperCase(),
                description: source.context || source.source_name,
                category: source.source_type_display_name || 'Other',
                categoryColor: getCategoryColor(
                  source.category_name || source.source_type_display_name
                ),
                status: (source.connection_status === 'connected'
                  ? 'connected'
                  : 'available') as 'connected' | 'available',
                statusColor:
                  source.connection_status === 'connected' ? 'green' : 'orange',
                endpoint: source.source_location,
                ingestionMethod: getDisplayIngestionMethod(source.ingestion_method || ''),
                lastSync: source.last_sync_utc || 'Never',
                apiId: source.id,
                sourceId: source.id,
                // Additional API fields
                source_name: source.source_name,
                data_category_id: source.data_category_id,
                update_frequency: source.update_frequency,
                source_location: source.source_location,
                context: source.context,
                connection_status: source.connection_status,
                priority: source.priority,
                last_sync_utc: source.last_sync_utc,
                created_at: source.created_at,
                updated_at: source.updated_at,
                created_by: source.created_by,
                updated_by: source.updated_by,
                deleted_at: source.deleted_at,
                deleted_by: source.deleted_by,
                category_name: source.category_name,
                category_code: source.category_code,
                category_type: source.category_type,
                source_type_id: source.source_type_id,
                source_type_code: source.source_type_code,
                source_type_display_name: source.source_type_display_name,
                source_type_category: source.source_type_category,
                first_upload_id: source.first_upload_id,
                arrival_schedule: source.arrival_schedule,
              }) as DataSourceCard
          )
          setDataSources(transformedSources)
        }

        // Mark as initialized successfully
        setHasInitializedData(true)
      } catch (error: any) {
        const errorMessage = extractErrorMessage(
          error,
          'Error fetching data sources and categories'
        )
        console.error(errorMessage)
        // Reset ref on error so it can be retried
        isInitializingRef.current = false
      } finally {
        setIsLoadingDataSources(false)
      }
    }

    fetchData()
  }, [hasInitializedData])

  const handleAddCustomSource = () => {
    onShowAddCustomDataSource?.()
  }

  const handleSourceClick = React.useCallback((e: React.MouseEvent, sourceId: string) => {
    // Prevent event bubbling and default behavior
    e.preventDefault()
    e.stopPropagation()

    // Prevent multiple rapid clicks
    if (isHandlingSourceClick) {
      return
    }

    setIsHandlingSourceClick(true)

    const source = dataSources.find(s => s.id === sourceId)
    if (source) {
      // Call the callback to show the data source in AddCustomDataSource
      onShowDataSourceDetails?.(source)
    }

    // Reset the flag after a short delay
    setTimeout(() => {
      setIsHandlingSourceClick(false)
    }, 500)
  }, [dataSources, onShowDataSourceDetails, isHandlingSourceClick])

  // Drag and Drop handlers for priority reordering
  const handlePriorityDragStart = (e: React.DragEvent, sourceId: string) => {
    setPriorityUpdateError('') // Clear any previous errors
    setPriorityUpdateSuccess('') // Clear any previous success messages
    setDraggedItem(sourceId)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', sourceId)
  }

  const handlePriorityDragOver = (
    e: React.DragEvent,
    targetSourceId: string
  ) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverItem(targetSourceId)
  }

  const handlePriorityDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOverItem(null)
  }

  const handlePriorityDrop = (e: React.DragEvent, targetSourceId: string) => {
    e.preventDefault()

    if (!draggedItem || draggedItem === targetSourceId) {
      setDraggedItem(null)
      return
    }

    // Work with the current filtered list
    const currentList = [...filteredPriorityDataSources]
    const draggedIndex = currentList.findIndex(s => s.id === draggedItem)
    const targetIndex = currentList.findIndex(s => s.id === targetSourceId)

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null)
      return
    }

    // Remove dragged item and insert at target position
    const [draggedSource] = currentList.splice(draggedIndex, 1)
    currentList.splice(targetIndex, 0, draggedSource)

    // Assign new priority values based on the new order (1-based indexing)
    const reorderedList = currentList.map((source, index) => ({
      ...source,
      priority: index + 1,
    }))

    // Update ONLY the dataSources state with new priorities for items in the current category
    setDataSources(prevSources => {
      return prevSources.map(source => {
        const updatedPriority = reorderedList.find(
          item => item.id === source.id
        )
        if (updatedPriority) {
          return { ...source, priority: updatedPriority.priority }
        }
        return source
      })
    })

    // Mark as having unsaved changes
    setHasUnsavedChanges(true)
    setPriorityUpdateError('') // Clear any previous errors
    setPriorityUpdateSuccess('') // Clear any previous success messages

    setDraggedItem(null)
    setDragOverItem(null)
  }

  const handleDragEnd = () => {
    setDraggedItem(null)
    setDragOverItem(null)
  }

  // Save priority changes to API
  const handleSavePriorities = async () => {
    try {
      setIsUpdatingPriorities(true)
      setPriorityUpdateError('')
      setPriorityUpdateSuccess('')

      // Use the filtered list directly
      const currentList = filteredPriorityDataSources

      // Get the data category ID for the selected category
      const selectedCategory = dataCategories.find(
        cat => cat.name === selectedDataSource
      )
      if (!selectedCategory) {
        setPriorityUpdateError(
          'Please select a valid data category before saving priorities.'
        )
        setIsUpdatingPriorities(false)
        return
      }

      // Prepare API payload - only include items from the current filtered list
      const prioritiesPayload = currentList
        .filter(source => {
          // Find matching data source to get the API ID
          const matchingDataSource = dataSources.find(ds => ds.id === source.id)

          // Ensure we have a valid numeric apiId
          const hasValidApiId =
            matchingDataSource &&
            matchingDataSource.apiId !== null &&
            matchingDataSource.apiId !== undefined &&
            !isNaN(Number(matchingDataSource.apiId))

          if (!hasValidApiId) {
            console.warn(
              'Skipping source with invalid apiId:',
              source.fullName,
              matchingDataSource?.apiId
            )
          }

          return hasValidApiId
        })
        .map((source, index) => {
          const matchingDataSource = dataSources.find(ds => ds.id === source.id)
          const dataSourceId = Number(matchingDataSource!.apiId!)

          // Validate that the conversion was successful
          if (isNaN(dataSourceId)) {
            console.error(
              'Invalid data_source_id:',
              matchingDataSource!.apiId,
              'for source:',
              source.fullName
            )
            throw new Error(`Invalid data source ID for ${source.fullName}`)
          }

          return {
            data_source_id: dataSourceId,
            priority: source.priority || index + 1,
          }
        })

      // Call API to update priorities
      if (prioritiesPayload.length > 0) {
        await DataSourcesService.updatePriorities(
          prioritiesPayload,
          Number(selectedCategory.id)
        )

        // Update dataSources state to reflect new priorities
        setDataSources(sources => {
          return sources.map(source => {
            const updatedPriority = prioritiesPayload.find(
              p => p.data_source_id === Number(source.apiId)
            )
            if (updatedPriority) {
              return { ...source, priority: updatedPriority.priority }
            }
            return source
          })
        })

        // Update original order to current order (mark as saved)
        setOriginalPriorityOrder([...currentList])
        setHasUnsavedChanges(false)
        setPriorityUpdateSuccess(
          `Successfully updated priorities for ${prioritiesPayload.length} data source${prioritiesPayload.length > 1 ? 's' : ''}.`
        )

        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setPriorityUpdateSuccess('')
        }, 3000)
      }
    } catch (error: any) {
      console.error('Failed to update priorities:', error)
      setPriorityUpdateError(
        error.message || 'Failed to update priorities. Please try again.'
      )
    } finally {
      setIsUpdatingPriorities(false)
    }
  }

  // Cancel/Reset priority changes
  const handleCancelPriorities = () => {
    // Reset to original order
    if (originalPriorityOrder.length > 0) {
      // Update dataSources to revert changes
      setDataSources(prevSources => {
        return prevSources.map(source => {
          const originalItem = originalPriorityOrder.find(
            orig => orig.id === source.id
          )
          if (originalItem) {
            return { ...source, priority: originalItem.priority }
          }
          return source
        })
      })
    }

    setHasUnsavedChanges(false)
    setPriorityUpdateError('')
    setPriorityUpdateSuccess('')
  }

  // Delete handlers - Commented out due to functionality issues
  /*
  const handleDeleteClick = (source: DataSourceCard) => {
    setDataSourceToDelete(source)
    setConfirmDeleteOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!dataSourceToDelete) return

    try {
      setDeletingDataSourceId(dataSourceToDelete.id)
      setConfirmDeleteOpen(false)

      // Call the delete API using the apiId (which should be the actual database ID)
      const idToDelete = dataSourceToDelete.apiId?.toString() || dataSourceToDelete.id
      await DataSourcesService.deleteDataSource(idToDelete)

      // Remove from local state
      setDataSources(prevSources =>
        prevSources.filter(source => source.id !== dataSourceToDelete.id)
      )

      // Clear states
      setDataSourceToDelete(null)
      setDeletingDataSourceId(null)

    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Error deleting data source')
      console.error(errorMessage)
      setDeletingDataSourceId(null)
      // TODO: Show error message to user
      alert(`Failed to delete data source:\n${errorMessage}`)
    }
  }

  const handleDeleteCancel = () => {
    setConfirmDeleteOpen(false)
    setDataSourceToDelete(null)
  }
  */

  return (
    <div className="configure-data-sources">
      {/* Read-Only Mode Banner */}
      {isReadOnlyMode && (
        <div className="readonly-mode-banner-config">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#856404"
            strokeWidth="2"
          >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <span className="readonly-mode-text-config">
            Read-Only Mode: You can view data but cannot make any changes.
          </span>
        </div>
      )}

      {/* Available Data Sources Section - Separate Container */}
      <div className="available-sources-container">
        <div className="section-header">
          <h2 className="section-title">Available Data Sources</h2>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              className={`add-custom-btn ${isReadOnlyMode ? 'btn-disabled' : 'btn-enabled'}`}
              onClick={isReadOnlyMode ? undefined : handleAddCustomSource}
              disabled={isReadOnlyMode}
            >
              + Add Custom Source
            </button>
            {/* <button
              className="export-data-sources-btn"
              onClick={handleExportDataSources}
              disabled={isExportingDataSources}
              title="Export real database data (Customer Identity & Accounts) to CSV"
              style={{
                padding: '8px 16px',
                border: '1px solid #00C950',
                borderRadius: '6px',
                backgroundColor: '#00C950',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isExportingDataSources ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                opacity: isExportingDataSources ? 0.6 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              {isExportingDataSources ? '⏳ Exporting...' : 'Sync Bank Data'} {/* Button text changes based on loading state */}
            {/* </button> */} 
          </div>
        </div>

        {/* Search Bar */}
        <div
          className="search-bar-container"
          style={{ marginBottom: '20px', padding: '0 24px' }}
        >
          <div style={{ position: 'relative', maxWidth: '500px' }}>
            <input
              type="text"
              placeholder="Search by Name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px 12px 44px',
                fontSize: '14px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => (e.target.style.borderColor = '#3b82f6')}
              onBlur={e => (e.target.style.borderColor = '#e5e7eb')}
            />
            <svg
              style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '20px',
                height: '20px',
                color: '#9ca3af',
              }}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: '#9ca3af',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Data Source Cards Grid */}
        <div className="data-sources-grid">
          {isLoadingDataSources ? (
            <div className="loader-container">
              <Loader active inline="centered" size="large" />
              <p className="loading-text">Loading Data Sources...</p>
            </div>
          ) : filteredDataSources.length === 0 ? (
            <div className="no-data-container">
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M9 12h6m-6 4h6M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
              </svg>
              <p className="no-data-title">No Data Available</p>
              <p className="no-data-description">
                {searchQuery
                  ? `No data sources found matching "${searchQuery}"`
                  : 'No data sources found. Please add a custom source to get started.'}
              </p>
            </div>
          ) : (
            filteredDataSources.map(source => (
              <div
                key={source.id}
                className={`data-source-card data-source-card-enabled ${isHandlingSourceClick ? 'data-source-card-loading' : ''}`}
                onClick={(e) => handleSourceClick(e, source.id)}
                style={{
                  cursor: isHandlingSourceClick ? 'not-allowed' : 'pointer',
                  opacity: isHandlingSourceClick ? 0.6 : 1,
                }}
              >
                {/* Left section: Icon and Info */}
                <div className="source-left-section">
                  {/* Source Icon */}
                  <div className="source-icon">
                    <span className="source-initials">{source.shortName}</span>
                  </div>

                  {/* Source Info */}
                  <div className="source-info">
                    <h3 className="source-name">{source.name}</h3>
                    <p className="source-description">{source.description}</p>

                    {/* Category Tag */}
                    <div
                      className={`category-tag category-${source.categoryColor}`}
                    >
                      {getDisplayDataCategory(source.category_name || '')}
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                <div className={`status-badge status-${source.statusColor}`}>
                  {(source.connection_status === 'connected' ||
                    source.status === 'connected') && (
                    <>
                      <svg
                        className="status-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9 12l2 2 4-4" />
                      </svg>
                      FILE UPLOADED
                    </>
                  )}
                  {(source.connection_status === 'disconnected' ||
                    source.status === 'available') && (
                    <>
                      <svg
                        className="status-icon"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {source.connection_status === 'disconnected'
                        ? 'DISCONNECTED'
                        : 'AVAILABLE'}
                    </>
                  )}
                </div>

                {/* Delete Button - Commented out due to issues in other places */}
                {/*
                <button
                  className={`delete-datasource-btn ${deletingDataSourceId === source.id ? 'deleting' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card click
                    handleDeleteClick(source);
                  }}
                  title={isReadOnlyMode ? "Delete disabled in read-only mode" : "Delete data source"}
                  disabled={deletingDataSourceId === source.id || isReadOnlyMode}
                  style={{
                    opacity: isReadOnlyMode ? 0.5 : 1,
                    cursor: isReadOnlyMode ? 'not-allowed' : 'pointer',
                  }}
                >
                  {deletingDataSourceId === source.id ? (
                    <div className="spinner"></div>
                  ) : (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  )}
                </button>
                */}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Source Authority & Priority Section - Separate Container */}
      <div className="priority-container">
        <div className="priority-header">
          <h3 className="priority-title">
            Select Data Source Authority & Priority
          </h3>
        </div>

        <div className="priority-content">
          <p className="priority-description">
            {selectedDataSource
              ? `Showing data sources for "${getDisplayDataCategory(selectedDataSource)}" category. Drag to reorder, then click "Save Changes" to apply. Higher sources take priority in conflict resolution.`
              : 'Select a data category to view and prioritize available sources. Drag to reorder, then save changes. Higher sources take priority in conflict resolution.'}
          </p>

          {/* Loading Indicator */}
          {isUpdatingPriorities && (
            <div className="priority-update-loading-style">
              <div className="priority-loading-spinner"></div>
              Updating priorities...
            </div>
          )}

          {/* Success Message */}
          {priorityUpdateSuccess && (
            <div className="priority-update-success-style">
              <svg
                className="priority-success-icon"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              {priorityUpdateSuccess}
            </div>
          )}

          {/* Error Message */}
          {priorityUpdateError && (
            <div className="priority-update-error-style">
              <svg
                className="priority-error-icon"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <div className="priority-error-title">
                  Failed to update priorities
                </div>
                <div>{priorityUpdateError}</div>
              </div>
            </div>
          )}

          {/* Select Data Source and Add Source Controls */}
          <div className="priority-controls">
            <div className="priority-select-section">
              <label className="priority-select-label">
                Select Data Category
              </label>
              <div className="priority-select-container">
                <div className="priority-source-badge-select">
                  <span className="priority-source-short-select">
                    {selectedDataSource
                      ? getDisplayDataCategory(selectedDataSource).substring(0, 3).toUpperCase()
                      : 'CAT'}
                  </span>
                </div>
                <select
                  className={`priority-select ${isReadOnlyMode ? 'priority-select-disabled' : 'priority-select-enabled'}`}
                  value={selectedDataSource}
                  onChange={
                    isReadOnlyMode
                      ? undefined
                      : e => setSelectedDataSource(e.target.value)
                  }
                  disabled={isReadOnlyMode}
                >
                  {dataCategories
                    .filter(category => {
                      // Show categories that have display mappings or are explicitly allowed
                      const categoryName = category.name.toLowerCase()

                      // Prioritize categories that have display mappings
                      if (DATA_CATEGORY_DISPLAY_MAP.hasOwnProperty(category.name)) {
                        return true
                      }

                      // Exclude specific unwanted categories
                      return !categoryName.includes('customer') &&
                             !categoryName.includes('identity') &&
                             !categoryName.includes('transaction') &&
                             !categoryName.includes('risk') &&
                             !categoryName.includes('compliance')
                    })
                    .map(category => (
                      <option key={category.id} value={category.name}>
                        {getDisplayDataCategory(category.name)}
                      </option>
                    ))}
                </select>
                <svg
                  className="priority-select-arrow"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Save/Cancel Actions - Only show when there are unsaved changes and not in read-only mode */}
          {hasUnsavedChanges && !isReadOnlyMode && (
            <div className="priority-actions-container">
              <svg
                className="priority-actions-warning-icon"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="priority-actions-content">
                <div className="priority-actions-title">
                  You have unsaved changes
                </div>
                <div className="priority-actions-subtitle">
                  Save your changes to update the priority order, or cancel to
                  revert.
                </div>
              </div>
              <div className="priority-actions-buttons">
                <button
                  className="priority-cancel-btn"
                  onClick={handleCancelPriorities}
                  disabled={isUpdatingPriorities}
                >
                  Cancel
                </button>
                <button
                  className={`priority-save-btn ${isUpdatingPriorities ? 'priority-save-btn-loading' : ''}`}
                  onClick={handleSavePriorities}
                  disabled={isUpdatingPriorities}
                >
                  {isUpdatingPriorities && (
                    <div className="priority-save-spinner"></div>
                  )}
                  {isUpdatingPriorities ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          <div className="priority-list">
            {filteredPriorityDataSources.length === 0 && selectedDataSource ? (
              <div className="no-priority-sources-container">
                <svg
                  className="no-priority-sources-icon"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M9 12h6m-6 4h6M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
                </svg>
                <p className="no-priority-sources-title">
                  No data sources found for "{getDisplayDataCategory(selectedDataSource)}"
                </p>
                <p className="no-priority-sources-subtitle">
                  Try selecting a different data category or add a custom source
                  first.
                </p>
              </div>
            ) : (
              filteredPriorityDataSources.map(source => (
                <div
                  key={source.id}
                  className={`priority-item ${draggedItem === source.id ? 'dragging' : ''} ${dragOverItem === source.id ? 'drag-over' : ''} ${isUpdatingPriorities ? 'priority-item-updating' : ''} ${isReadOnlyMode ? 'priority-item-readonly' : ''} ${isUpdatingPriorities || isReadOnlyMode ? 'priority-item-disabled' : ''}`}
                  draggable={!isUpdatingPriorities && !isReadOnlyMode}
                  onDragStart={
                    isUpdatingPriorities || isReadOnlyMode
                      ? undefined
                      : e => handlePriorityDragStart(e, source.id)
                  }
                  onDragOver={
                    isUpdatingPriorities || isReadOnlyMode
                      ? undefined
                      : e => handlePriorityDragOver(e, source.id)
                  }
                  onDragLeave={
                    isUpdatingPriorities || isReadOnlyMode
                      ? undefined
                      : handlePriorityDragLeave
                  }
                  onDrop={
                    isUpdatingPriorities || isReadOnlyMode
                      ? undefined
                      : e => handlePriorityDrop(e, source.id)
                  }
                  onDragEnd={
                    isUpdatingPriorities || isReadOnlyMode
                      ? undefined
                      : handleDragEnd
                  }
                >
                  <div className="priority-item-content">
                    <svg
                      className="drag-handle"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="9" cy="5" r="1" />
                      <circle cx="9" cy="12" r="1" />
                      <circle cx="9" cy="19" r="1" />
                      <circle cx="15" cy="5" r="1" />
                      <circle cx="15" cy="12" r="1" />
                      <circle cx="15" cy="19" r="1" />
                    </svg>
                    <div
                      className={`priority-source-badge priority-badge-${source.shortName.toLowerCase()}`}
                    >
                      <span className="priority-source-short">
                        {source.shortName}
                      </span>
                    </div>
                    <div className="priority-source-info">
                      <h5 className="priority-source-name">
                        {source.fullName}
                      </h5>
                      <p className="priority-source-desc">
                        {source.description}
                      </p>
                    </div>
                  </div>
                  <span className="priority-badge">
                    {source.priority ? `Priority ${source.priority}` : '-'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog - Commented out due to functionality issues */}
      {/*
      <Confirm
        open={confirmDeleteOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        header="Delete Data Source"
        content={
          dataSourceToDelete
            ? `Are you sure you want to delete "${dataSourceToDelete.name}"? This action cannot be undone.`
            : ''
        }
        cancelButton="Cancel"
        confirmButton="Delete"
      />
      */}
    </div>
  )
}

export default ConfigureDataSources
